/**
 * Module dependencies.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Space, message, Empty, Spin, Card, Tag, Modal } from 'antd';
import { ReloadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from 'app/components/atoms/button/button';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { exportsApi } from 'app/core/api/exports-api';
import type { DataExport } from 'app/types/export';
import styles from './data-exports-drawer.module.less';
import { useExportsCount } from 'app/components/providers/exports-count-provider';

/**
 * `DataExportsDrawerProps` type.
 */

export interface DataExportsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Export `DataExportsDrawer` component.
 */

export function DataExportsDrawer({ open, onClose }: DataExportsDrawerProps): JSX.Element {
  const [exports, setExports] = useState<DataExport[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const client = useRequestProvider();
  const { refetch: refetchExportsCount } = useExportsCount();

  const markAsViewed = useCallback(async () => {
    try {
      await exportsApi.markExportsViewed(client);
      await refetchExportsCount();
    } catch (error) {
      console.error('Failed to mark exports as viewed:', error);
    }
  }, [client, refetchExportsCount]);

  const fetchExports = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    try {
      const response = await exportsApi.listExports(client, 1, 20);
      setExports(response.results);

      // Check if there are any pending or processing exports
      const hasActiveExports = response.results.some(exp => exp.status === 'pending' || exp.status === 'processing');
      setAutoRefresh(hasActiveExports);

      // Trigger refetch of exports count
      refetchExportsCount();
    } catch (error) {
      console.error('Failed to fetch exports:', error);
      message.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, [client, open, refetchExportsCount]);

  // Fetch exports and mark as viewed when drawer opens
  useEffect(() => {
    if (open) {
      fetchExports();
      markAsViewed();
    }
  }, [open, fetchExports, markAsViewed]);

  // Auto-refresh every 5 seconds if there are active exports
  useEffect(() => {
    if (!autoRefresh || !open) return;

    const interval = setInterval(() => {
      fetchExports();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, open, fetchExports]);

  const handleDownload = (exportId: string, filename: string, format: string) => {
    try {
      const url = exportsApi.getDownloadUrl(exportId);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download export');
    }
  };

  const handleDelete = (exportItem: DataExport) => {
    Modal.confirm({
      title: 'Delete Export',
      content: `Are you sure you want to delete "${exportItem.filename}"?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await exportsApi.deleteExport(client, exportItem.id);
          message.success('Export deleted successfully');
          fetchExports();
          refetchExportsCount();
        } catch (error) {
          console.error('Failed to delete export:', error);
          message.error('Failed to delete export');
        }
      }
    });
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: 'Clear All Exports',
      content: 'Are you sure you want to delete all completed exports? This action cannot be undone.',
      okText: 'Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const deletedCount = await exportsApi.clearAllExports(client);
          message.success(`Successfully deleted ${deletedCount} export(s)`);
          fetchExports();
          refetchExportsCount();
        } catch (error) {
          console.error('Failed to clear exports:', error);
          message.error('Failed to clear exports');
        }
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'processing';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Drawer
      destroyOnClose={false}
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => fetchExports()} size={'small'}>
            {'Refresh'}
          </Button>
          <Button danger disabled={exports.length === 0} onClick={handleClearAll} size={'small'}>
            {'Clear All'}
          </Button>
        </Space>
      }
      onClose={onClose}
      open={open}
      placement={'right'}
      title={
        <div className={styles.drawerHeader}>
          <span>{'Data Exports'}</span>
          {autoRefresh && <span className={styles.autoRefreshIndicator}>{'Auto-refreshing...'}</span>}
        </div>
      }
      width={600}
    >
      {loading && exports.length === 0 ? (
        <div className={styles.spinnerContainer}>
          <Spin size={'large'} />
        </div>
      ) : exports.length === 0 ? (
        <Empty description={'No exports found'} />
      ) : (
        <>
          <div className={styles.listTitle}>
            <h3>{'Export History'}</h3>
          </div>
          <div className={styles.cardList}>
            {exports.map(exportItem => (
              <Card className={styles.exportCard} key={exportItem.id} size={'small'}>
                <div className={styles.cardContent}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardTitle}>
                      <strong>{exportItem.filename}</strong>
                      <Tag color={getStatusColor(exportItem.status)} style={{ marginLeft: 8 }}>
                        {exportItem.status.toUpperCase()}
                      </Tag>
                    </div>
                    <div className={styles.cardActions}>
                      {exportItem.status === 'completed' && (
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownload(exportItem.id, exportItem.filename, exportItem.format)}
                          size={'small'}
                          type={'primary'}
                        />
                      )}
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(exportItem)}
                        size={'small'}
                      />
                    </div>
                  </div>

                  <div className={styles.cardMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>{'Format:'}</span>
                      <span className={styles.metaValue}>{exportItem.format.toUpperCase()}</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>{'Resource:'}</span>
                      <span className={styles.metaValue}>{exportItem.resourceName}</span>
                    </div>
                    {exportItem.totalRecords !== null && exportItem.totalRecords !== undefined && (
                      <div className={styles.metaItem}>
                        <span className={styles.metaLabel}>{'Records:'}</span>
                        <span className={styles.metaValue}>{exportItem.totalRecords.toLocaleString()}</span>
                      </div>
                    )}
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>{'Created:'}</span>
                      <span className={styles.metaValue}>
                        <DateTime date={exportItem.createdAt} />
                      </span>
                    </div>
                    {exportItem.errorMessage && (
                      <div className={styles.errorMessage}>
                        <span className={styles.metaLabel}>{'Error:'}</span>
                        <span className={styles.metaValue}>{exportItem.errorMessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}
